import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';

import type { Database } from '@/integrations/supabase/types';

export type NotificationType = Database['public']['Enums']['notification_type'];

export type NotificationCategory = Database['public']['Enums']['notification_category'];

/**
 * Every column {@link Notification} declares, named explicitly. Kept in sync
 * with that interface by hand rather than fetched with '*': these rows carry
 * user-facing title/message content, so widening what reaches the client must
 * be a decision, not a side effect of a migration.
 *
 * `metadata` as a whole is deliberately absent. The producer packs constituent
 * detail into it - sender name and address, email subject, rejection reason -
 * so only the one key the list shows is read: the sharer's note on a
 * correspondence share, which the recipient can already read on the share.
 *
 * `select` is given the row type: inferring it from this list trips TS2589.
 */
const NOTIFICATION_COLUMNS = `
  id,
  organization_id,
  recipient_user_id,
  sender_user_id,
  notification_type,
  category,
  title,
  message,
  action_url,
  related_entity_type,
  related_entity_id,
  read_at,
  dismissed_at,
  saved_at,
  created_at,
  updated_at,
  share_message:metadata->>share_message
`;

export interface Notification {
  id: string;
  organization_id: string;
  recipient_user_id: string;
  sender_user_id: string | null;
  notification_type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  action_url: string | null;
  related_entity_type: string | null;
  related_entity_id: string | null;
  read_at: string | null;
  dismissed_at: string | null;
  saved_at: string | null;
  created_at: string;
  updated_at: string;
  share_message?: string | null;
  sender?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

interface UseNotificationsOptions {
  category?: NotificationCategory;
  unreadOnly?: boolean;
  includeDismissed?: boolean;
  doneOnly?: boolean;
  savedOnly?: boolean;
  limit?: number;
  offset?: number;
}

/** Every dimension that changes WHICH notifications a fetch returns. */
interface NotificationsView {
  readonly organizationId: string;
  readonly recipientUserId: string;
  readonly category: NotificationCategory | undefined;
  readonly unreadOnly: boolean;
  readonly includeDismissed: boolean;
  readonly doneOnly: boolean;
  readonly savedOnly: boolean;
  readonly limit: number;
  readonly offset: number;
}

/** One page of notifications, with the server's exact count for the view. */
export interface NotificationsPage {
  readonly items: Notification[];
  readonly totalCount: number;
}

async function fetchNotificationsPage(view: NotificationsView): Promise<NotificationsPage> {
  let query = supabase
    .from('notifications')
    .select<typeof NOTIFICATION_COLUMNS, Notification>(NOTIFICATION_COLUMNS, { count: 'exact' })
    .eq('organization_id', view.organizationId)
    .eq('recipient_user_id', view.recipientUserId)
    .order('created_at', { ascending: false })
    .range(view.offset, view.offset + view.limit - 1);

  if (view.savedOnly) {
    // Saved view: only bookmarked notifications (regardless of dismissed state)
    query = query.not('saved_at', 'is', null);
  } else if (view.doneOnly) {
    // Done view: only dismissed notifications
    query = query.not('dismissed_at', 'is', null);
  } else if (!view.includeDismissed) {
    // Inbox view (default): only non-dismissed
    query = query.is('dismissed_at', null);
  }

  if (view.category) {
    query = query.eq('category', view.category);
  }

  if (view.unreadOnly) {
    query = query.is('read_at', null);
  }

  const { data, count, error } = await query;

  if (error) {
    throw error;
  }

  return { items: data ?? [], totalCount: count ?? 0 };
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  const {
    category,
    unreadOnly = false,
    includeDismissed = false,
    doneOnly = false,
    savedOnly = false,
    limit = 50,
    offset = 0,
  } = options;

  return useQuery({
    queryKey: [
      'notifications',
      currentOrganizationId,
      user?.id,
      category,
      unreadOnly,
      includeDismissed,
      doneOnly,
      savedOnly,
      limit,
      offset,
    ],
    queryFn: async (): Promise<NotificationsPage> => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return { items: [], totalCount: 0 };
      }

      return await fetchNotificationsPage({
        organizationId: currentOrganizationId,
        recipientUserId: user.id,
        category,
        unreadOnly,
        includeDismissed,
        doneOnly,
        savedOnly,
        limit,
        offset,
      });
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000, // 30 seconds
    // Avoids the flash of empty state on filter and page transitions, but only
    // WITHIN one org and user: the query key carries both, and keeping the
    // previous page across an org switch would render one organisation's
    // notification titles under another's - for as long as the replacement
    // query stays pending.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === currentOrganizationId && previousQuery.queryKey[2] === user?.id
        ? previous
        : undefined,
  });
}

/**
 * Lightweight query that returns the IDs of every read-but-not-dismissed
 * notification for the current user/org — no row limit. Used by the
 * "Dismiss all read" bulk action so it operates on the full inbox, not
 * just the slice rendered in the popover.
 */
export function useReadDismissibleNotificationIds() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  return useQuery({
    queryKey: ['notifications', 'read-dismissible-ids', currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return [];
      }

      const { data, error } = await supabase
        .from('notifications')
        .select('id')
        .eq('organization_id', currentOrganizationId)
        .eq('recipient_user_id', user.id)
        .not('read_at', 'is', null)
        .is('dismissed_at', null);

      if (error) {
        throw error;
      }

      return (data ?? []).map((row) => row.id);
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000,
  });
}

export function useUnreadNotificationCount() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  return useQuery({
    queryKey: ['notifications', 'unread-count', currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        return 0;
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', currentOrganizationId)
        .eq('recipient_user_id', user.id)
        .is('read_at', null)
        .is('dismissed_at', null);

      if (error) {
        throw error;
      }

      return count || 0;
    },
    enabled: !!user?.id && !!currentOrganizationId && currentOrganizationId !== 'admin',
    staleTime: 30 * 1000,
  });
}
